import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, {
  type CSSProperties,
  type FC,
  useCallback,
  useMemo,
  useState,
} from 'react';

import globalFeaturesGql from '../gql/globalFeatures.gql';

interface AnnouncementBannerConfig {
  bannerTextClassName?: string;
  bannerTextStyle?: CSSProperties;
  className?: string;
  darkGradient?: boolean;
  hideInIframe?: boolean;
  style?: CSSProperties;
}

export const AnnouncementBanner: FC = () => {
  const { data } = useQuery(globalFeaturesGql, {
    context: {
      isPublic: true,
    },
    variables: {
      only: [
        'announcementBanner',
        'announcementBannerConfig',
        'marketingAnnouncementBanner',
        'marketingAnnouncementBannerConfig',
      ],
    },
  });
  const announcementBanner =
    data?.globalFeatures?.marketingAnnouncementBanner ||
    data?.globalFeatures?.announcementBanner;

  const configString =
    data?.globalFeatures?.marketingAnnouncementBannerConfig ||
    data?.globalFeatures?.announcementBannerConfig;

  const config: AnnouncementBannerConfig = useMemo(() => {
    if (!configString) {
      return {};
    }
    try {
      return JSON.parse(configString);
    } catch (e) {
      console.error('Failed to parse announcement banner config:', e);
      return {};
    }
  }, [configString]);

  const [announcementBannerId, announcementBannerContent] = useMemo(() => {
    if (!announcementBanner) {
      return [null, null];
    }
    const match = /(?:(\w+)\s*:\s*)?(.+)/.exec(announcementBanner);
    if (!match) {
      return [null, null];
    }
    const [, id, content] = match;
    return [id || content, content];
  }, [announcementBanner]);

  const [viewedAnnouncement, setViewedAnnouncement] = useState(() =>
    typeof localStorage === 'undefined'
      ? null
      : localStorage.getItem('viewedAnnouncement'),
  );

  const dismissAnnouncement = useCallback(() => {
    if (!announcementBannerId) {
      return;
    }
    localStorage.setItem('viewedAnnouncement', announcementBannerId);
    setViewedAnnouncement(announcementBannerId);
  }, [announcementBannerId]);

  return announcementBannerContent &&
    announcementBannerId &&
    announcementBannerId !== viewedAnnouncement ? (
    <div
      className={classNames(
        'flex flex-row items-center p-4 min-h-[60px] bg-gradient-to-r text-medium font-medium leading-5',
        config.darkGradient
          ? 'from-[#6447FB] to-[#FF6767] text-white'
          : 'from-[#FFECE3] to-[#FFCDDC] text-black',
        config.className,
      )}
      style={config.style}
    >
      <div
        dangerouslySetInnerHTML={{
          __html: announcementBannerContent,
        }}
        className={classNames(
          'flex-auto [&>a]:underline',
          config.darkGradient ? '[&>a]:text-white' : '[&>a]:text-black',
          config.bannerTextClassName,
        )}
        style={config.bannerTextStyle}
      />
      <div
        className={classNames(
          'cursor-pointer flex-none',
          config.darkGradient
            ? 'text-gray-200 hover:text-white'
            : 'text-gray-700 hover:text-black',
        )}
        onClick={dismissAnnouncement}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.5303 4.53033C12.8232 4.23744 12.8232 3.76256 12.5303 3.46967C12.2374 3.17678 11.7626 3.17678 11.4697 3.46967L8 6.93934L4.53033 3.46967C4.23744 3.17678 3.76256 3.17678 3.46967 3.46967C3.17678 3.76256 3.17678 4.23744 3.46967 4.53033L6.93934 8L3.46967 11.4697C3.17678 11.7626 3.17678 12.2374 3.46967 12.5303C3.76256 12.8232 4.23744 12.8232 4.53033 12.5303L8 9.06066L11.4697 12.5303C11.7626 12.8232 12.2374 12.8232 12.5303 12.5303C12.8232 12.2374 12.8232 11.7626 12.5303 11.4697L9.06066 8L12.5303 4.53033Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  ) : null;
};
